import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { FaSave, FaAngleLeft, FaTrash } from 'react-icons/fa';
import { MdAdd } from 'react-icons/md';
import { IoIosArrowForward } from 'react-icons/io';
import { RiRefreshLine } from 'react-icons/ri';
import LeaseApiService from '../../services/apiService';

const LISTING_ROUTE = '/Lease/Management/Lease/GeneralInvoice/Listing';

// Cached for the session so the form does not re-fetch these on every open.
let cachedDropdowns = null;
let cachedTaxCodes = null;

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
    }),
};

const toOption = (item) => (item ? { label: item.text, value: item } : null);
const toTenantOption = (item) => (item ? { label: `${item.value} - ${item.text}`, value: item } : null);
const toAccountCodeOption = (item) => (item ? { label: item.value, value: item } : null);
const toAccountNameOption = (item) => (item ? { label: item.text, value: item } : null);
const toTaxOption = (tax) => (tax ? { label: tax.description, value: tax } : null);

const computeLineAmounts = (enteredAmount, taxCodeValue, inclusive) => {
    const amount = parseFloat(enteredAmount) || 0;
    const rate = typeof taxCodeValue?.rate === 'number' ? taxCodeValue.rate : 0;
    if (!amount) return { vat: 0, total: 0, creditAmount: 0 };
    let vat = 0;
    let total = 0;
    let creditAmount = 0;
    if (inclusive) {
        vat = (amount * rate) / (100 + rate);
        creditAmount = amount - vat;
        total = amount;
    } else {
        vat = (amount * rate) / 100;
        creditAmount = amount;
        total = amount + vat;
    }
    return {
        vat: Number(vat.toFixed(3)),
        total: Number(total.toFixed(3)),
        creditAmount: Number(creditAmount.toFixed(3)),
    };
};

const createEmptyLine = (taxCodes) => {
    const none = taxCodes?.find((t) => t.taxCode === '0') || taxCodes?.[0] || null;
    return {
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        acCode: null,
        enteredAmount: '',
        taxCode: toTaxOption(none),
        inclusive: false,
        actual: 0,
        narration: '',
        vat: 0,
        total: 0,
        creditAmount: 0,
    };
};

const emptyHeader = () => ({
    unitClass: null,
    tenant: null,
    property: null,
    unit: null,
    showAllLeases: false,
    leaseNo: null,
    invoiceType: null,
    date: new Date(),
    amount: '',
    commonNarration: '',
    isAdjustment: false,
});

const formatAmount = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(3) : '0.000';
};

const LeaseGeneralInvoiceForm = ({ mode = 'create' }) => {
    const apiService = new LeaseApiService();
    const navigate = useNavigate();
    const { voucherNo: voucherNoParam } = useParams();
    const [searchParams] = useSearchParams();
    const financialYearParam = searchParams.get('financialYear') || '';

    const isCreate = mode === 'create';
    const isView = mode === 'view';
    const isEdit = mode === 'edit';

    const [loadState, setLoadState] = useState(isCreate ? 'ready' : 'loading');
    const [loadErrorMessage, setLoadErrorMessage] = useState('');
    const [loadedHeader, setLoadedHeader] = useState(null);

    const [dropdowns, setDropdowns] = useState({ InvoiceTypes: [], Accounts: [], Tenants: [], UnitClasses: [] });
    const [taxCodes, setTaxCodes] = useState([]);
    const [properties, setProperties] = useState([]);
    const [units, setUnits] = useState([]);
    const [leaseNos, setLeaseNos] = useState([]);
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [loadingLeaseNos, setLoadingLeaseNos] = useState(false);

    const [header, setHeader] = useState(emptyHeader());
    const [lines, setLines] = useState([createEmptyLine([])]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [headerTab, setHeaderTab] = useState(true);
    const [linesTab, setLinesTab] = useState(true);

    const propertyReqId = useRef(0);
    const unitReqId = useRef(0);
    const leaseReqId = useRef(0);

    const fetchCascade = async (dropdownFor, paramsObj) => {
        const params = new URLSearchParams();
        params.append('dropdownFor', dropdownFor);
        Object.entries(paramsObj || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') params.append(key, value);
        });
        const res = await apiService.getApiParamater('GEN_INV_CASCADE', `?${params.toString()}`);
        if (res?.status === 204) return [];
        return res?.data?.items || [];
    };

    const loadProperties = async (paramsObj) => {
        const reqId = ++propertyReqId.current;
        setLoadingProperties(true);
        try {
            const list = await fetchCascade('Properties', paramsObj);
            if (reqId === propertyReqId.current) setProperties(list);
            return list;
        } catch (error) {
            if (reqId === propertyReqId.current) setProperties([]);
            return [];
        } finally {
            if (reqId === propertyReqId.current) setLoadingProperties(false);
        }
    };

    const loadUnits = async (paramsObj) => {
        const reqId = ++unitReqId.current;
        setLoadingUnits(true);
        try {
            const list = await fetchCascade('Units', paramsObj);
            if (reqId === unitReqId.current) setUnits(list);
            return list;
        } catch (error) {
            if (reqId === unitReqId.current) setUnits([]);
            return [];
        } finally {
            if (reqId === unitReqId.current) setLoadingUnits(false);
        }
    };

    const loadLeaseNos = async (paramsObj, extraOption) => {
        const reqId = ++leaseReqId.current;
        setLoadingLeaseNos(true);
        try {
            let list = await fetchCascade('LeaseNos', paramsObj);
            if (extraOption && !list.some((l) => l.value === extraOption.value)) {
                list = [extraOption, ...list];
            }
            if (reqId === leaseReqId.current) setLeaseNos(list);
            return list;
        } catch (error) {
            if (reqId === leaseReqId.current) setLeaseNos([]);
            return [];
        } finally {
            if (reqId === leaseReqId.current) setLoadingLeaseNos(false);
        }
    };

    const loadMasterData = async () => {
        try {
            let dd = cachedDropdowns;
            if (!dd) {
                const res = await apiService.post('GEN_INV_DROPDOWNS', { dropdownTypes: ['InvoiceTypes', 'Accounts', 'Tenants', 'UnitClasses'] });
                dd = res?.data?.dropdowns || {};
                cachedDropdowns = dd;
            }
            setDropdowns(dd);

            let tax = cachedTaxCodes;
            if (!tax) {
                const taxRes = await apiService.get('GEN_INV_TAXCODES');
                tax = taxRes?.data?.result ?? taxRes?.data?.data ?? [];
                cachedTaxCodes = tax;
            }
            setTaxCodes(tax);

            if (isCreate) {
                const unitClassItem = (dd.UnitClasses || []).find((u) => u.value === 'Lease') || (dd.UnitClasses || [])[0];
                if (unitClassItem) {
                    setHeader((prev) => ({ ...prev, unitClass: toOption(unitClassItem) }));
                    loadProperties({ unitClass: unitClassItem.value });
                }
                setLines([createEmptyLine(tax)]);
            }
            return { dropdowns: dd, taxCodes: tax };
        } catch (error) {
            console.error('Error loading master data:', error);
            toast.error('Something went wrong. Please try again.');
            return { dropdowns: {}, taxCodes: [] };
        }
    };

    const applyLoadedHeader = async (h, apiLines, master) => {
        const dd = master?.dropdowns || {};
        const tax = master?.taxCodes || [];

        const unitClassItem = (dd.UnitClasses || []).find((u) => u.value === h.unitClass);
        const tenantItem = (dd.Tenants || []).find((t) => t.value === h.tenantNo);
        const invoiceTypeItem = (dd.InvoiceTypes || []).find((t) => t.value === h.invoiceType);

        setHeader({
            unitClass: unitClassItem ? toOption(unitClassItem) : (h.unitClass ? toOption({ value: h.unitClass, text: h.unitClass }) : null),
            tenant: tenantItem ? toTenantOption(tenantItem) : (h.tenantNo ? toTenantOption({ value: h.tenantNo, text: h.tenantName }) : null),
            property: h.propertyNo ? toOption({ value: h.propertyNo, text: h.propertyNo }) : null,
            unit: h.unitNo ? toOption({ value: h.unitNo, text: h.unitNo }) : null,
            showAllLeases: false,
            leaseNo: h.leaseNo ? toOption({ value: h.leaseNo, text: h.leaseNo }) : null,
            invoiceType: invoiceTypeItem ? toOption(invoiceTypeItem) : (h.invoiceType ? toOption({ value: h.invoiceType, text: h.invoiceType }) : null),
            date: h.postingDate ? new Date(h.postingDate) : new Date(),
            amount: h.amount ?? '',
            commonNarration: h.commonNarration || '',
            isAdjustment: !!h.isAdjustment,
        });

        setLines(
            (apiLines?.length ? apiLines : []).map((l) => {
                const acItem = (dd.Accounts || []).find((a) => a.value === l.acCode);
                const taxItem = (tax || []).find((t) => t.taxCode === l.taxCode);
                const { vat, total, creditAmount } = computeLineAmounts(l.enteredAmount, taxItem, l.inclusive);
                return {
                    key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    acCode: acItem || (l.acCode ? { value: l.acCode, text: l.acName } : null),
                    enteredAmount: l.enteredAmount ?? '',
                    taxCode: taxItem ? toTaxOption(taxItem) : null,
                    inclusive: !!l.inclusive,
                    actual: l.actual || 0,
                    narration: l.narration || '',
                    vat: l.vat ?? vat,
                    total: l.total ?? total,
                    creditAmount: l.creditAmount ?? creditAmount,
                };
            })
        );

        if (h.unitClass) {
            await loadProperties({ unitClass: h.unitClass, tenantNo: h.tenantNo });
        }
        if (h.unitClass && h.propertyNo) {
            await loadUnits({ unitClass: h.unitClass, propertyNo: h.propertyNo, tenantNo: h.tenantNo });
        }
        if (h.propertyNo || h.unitNo || h.tenantNo) {
            await loadLeaseNos(
                { tenantNo: h.tenantNo, propertyNo: h.propertyNo, unitNo: h.unitNo, showAll: true },
                h.leaseNo ? { value: h.leaseNo, text: h.leaseNo } : null
            );
        }
    };

    const loadInvoice = async (master) => {
        setLoadState('loading');
        try {
            const res = await apiService.getGeneralInvoiceByVoucherNo(voucherNoParam, financialYearParam);
            const statusCode = res?.data?.statusCode ?? res?.status;
            if (statusCode === 404) {
                setLoadState('notfound');
                return;
            }
            if (statusCode !== 200) {
                setLoadState('error');
                setLoadErrorMessage(res?.data?.message || 'Something went wrong while loading the invoice.');
                return;
            }
            const data = res?.data?.result ?? res?.data?.data;
            const h = data?.header || {};
            setLoadedHeader(h);
            await applyLoadedHeader(h, data?.lines || [], master);
            setLoadState('ready');
        } catch (error) {
            console.error('Error loading invoice:', error);
            const statusCode = error?.response?.status;
            if (statusCode === 404) {
                setLoadState('notfound');
            } else {
                setLoadState('error');
                setLoadErrorMessage('Something went wrong while loading the invoice. Please try again.');
            }
        }
    };

    useEffect(() => {
        (async () => {
            const master = await loadMasterData();
            if (!isCreate) {
                await loadInvoice(master);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUnitClassChange = (opt) => {
        setHeader((prev) => ({ ...prev, unitClass: opt, property: null, unit: null, leaseNo: null, showAllLeases: false }));
        setUnits([]);
        setLeaseNos([]);
        loadProperties({ unitClass: opt?.value?.value, tenantNo: header.tenant?.value?.value });
    };

    const handleTenantChange = (opt) => {
        setHeader((prev) => ({ ...prev, tenant: opt, property: null, unit: null, leaseNo: null, showAllLeases: false }));
        setUnits([]);
        setLeaseNos([]);
        loadProperties({ unitClass: header.unitClass?.value?.value, tenantNo: opt?.value?.value });
    };

    const handlePropertyChange = (opt) => {
        setHeader((prev) => ({ ...prev, property: opt, unit: null, leaseNo: null, showAllLeases: false }));
        setLeaseNos([]);
        loadUnits({ unitClass: header.unitClass?.value?.value, propertyNo: opt?.value?.value, tenantNo: header.tenant?.value?.value });
    };

    const handleUnitChange = async (opt) => {
        setHeader((prev) => ({ ...prev, unit: opt, leaseNo: null }));
        const unitClass = header.unitClass?.value?.value;
        const propertyNo = header.property?.value?.value;
        const unitNo = opt?.value?.value;
        const tenantNo = header.tenant?.value?.value;

        const list = await loadLeaseNos({ tenantNo, propertyNo, unitNo, showAll: header.showAllLeases });

        if (!tenantNo && unitNo) {
            try {
                const params = new URLSearchParams();
                params.append('propertyNo', propertyNo || '');
                params.append('unitNo', unitNo);
                params.append('unitClass', unitClass || '');
                const res = await apiService.getApiParamater('GEN_INV_TENANT_BY_UNIT', `?${params.toString()}`);
                const data = res?.data?.result ?? res?.data?.data;
                if (data?.leaseNo) {
                    const tenantItem = dropdowns?.Tenants?.find((t) => t.value === data.tenantNo);
                    const leaseOpt = { value: data.leaseNo, text: data.leaseNo };
                    if (!list.some((l) => l.value === data.leaseNo)) {
                        setLeaseNos((prev) => [leaseOpt, ...prev]);
                    }
                    setHeader((prev) => ({
                        ...prev,
                        tenant: tenantItem ? toTenantOption(tenantItem) : toTenantOption({ value: data.tenantNo, text: data.tenantName }),
                        leaseNo: toOption(leaseOpt),
                    }));
                }
            } catch (error) {
                // 404 means no lease was found for this unit; leave the fields untouched.
            }
        }
    };

    const handleShowAllLeasesToggle = (checked) => {
        setHeader((prev) => ({ ...prev, showAllLeases: checked }));
        loadLeaseNos({
            tenantNo: header.tenant?.value?.value,
            propertyNo: header.property?.value?.value,
            unitNo: header.unit?.value?.value,
            showAll: checked,
        });
    };

    const handleLeaseNoChange = (opt) => {
        setHeader((prev) => ({ ...prev, leaseNo: opt }));
        setErrors((prev) => ({ ...prev, leaseNo: undefined }));
    };

    const handleInvoiceTypeChange = (opt) => {
        setHeader((prev) => ({ ...prev, invoiceType: opt }));
        setErrors((prev) => ({ ...prev, invoiceType: undefined }));
    };

    const handleDateChange = (date) => {
        setHeader((prev) => ({ ...prev, date }));
        setErrors((prev) => ({ ...prev, date: undefined }));
    };

    const handleAmountChange = (value) => {
        setHeader((prev) => ({ ...prev, amount: value }));
        setErrors((prev) => ({ ...prev, amount: undefined }));
    };

    const handleNarrationChange = (value) => {
        setHeader((prev) => ({ ...prev, commonNarration: value }));
    };

    const handleAdjustmentToggle = (checked) => {
        setHeader((prev) => ({ ...prev, isAdjustment: checked }));
    };

    const updateLine = (index, patch) => {
        setLines((prev) => {
            const updated = [...prev];
            const row = { ...updated[index], ...patch };
            const { vat, total, creditAmount } = computeLineAmounts(row.enteredAmount, row.taxCode?.value, row.inclusive);
            row.vat = vat;
            row.total = total;
            row.creditAmount = creditAmount;
            updated[index] = row;
            return updated;
        });
        setErrors((prev) => ({ ...prev, lines: undefined }));
    };

    const handleAddLine = () => {
        setLines((prev) => [...prev, createEmptyLine(taxCodes)]);
    };

    const handleRemoveLine = (index) => {
        setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
    };

    const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.creditAmount) || 0), 0);
    const totalVat = lines.reduce((sum, l) => sum + (parseFloat(l.vat) || 0), 0);
    const totalAmount = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);

    const validate = () => {
        const newErrors = {};
        if (!header.leaseNo?.value) newErrors.leaseNo = 'Select Lease No.';
        if (!header.invoiceType?.value) newErrors.invoiceType = 'Select Invoice Type.';
        if (!header.date) newErrors.date = 'Select Date.';
        const amount = parseFloat(header.amount);
        if (!amount || amount <= 0) newErrors.amount = 'Enter Amount greater than 0.';

        const validLines = lines.filter((l) => (parseFloat(l.enteredAmount) || 0) > 0);
        if (validLines.length === 0) {
            newErrors.lines = 'Add at least one line with an amount.';
        } else if (validLines.some((l) => !l.acCode)) {
            newErrors.lines = 'Select an Account Code for every line with an amount.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildBody = () => {
        const body = {
            unitClass: header.unitClass?.value?.value || '',
            tenantNo: header.tenant?.value?.value || '',
            propertyNo: header.property?.value?.value || '',
            unitNo: header.unit?.value?.value || '',
            leaseNo: header.leaseNo?.value?.value || '',
            invoiceType: header.invoiceType?.value?.value || '',
            postingDate: moment(header.date).format('YYYY-MM-DD'),
            amount: Number(parseFloat(header.amount).toFixed(3)),
            commonNarration: header.commonNarration || '',
            isAdjustment: !!header.isAdjustment,
            lines: lines
                .filter((l) => (parseFloat(l.enteredAmount) || 0) > 0)
                .map((l) => ({
                    acCode: l.acCode?.value || '',
                    enteredAmount: Number(parseFloat(l.enteredAmount).toFixed(3)),
                    taxCode: l.taxCode?.value?.taxCode ?? '0',
                    inclusive: !!l.inclusive,
                    actual: l.actual || 0,
                    narration: l.narration || '',
                })),
        };
        if (isEdit) {
            body.financialYear = loadedHeader?.financialYear || financialYearParam;
        }
        return body;
    };

    const handleSaveClick = () => {
        if (!validate()) return;
        setShowConfirm(true);
    };

    const handleConfirmSave = async () => {
        setSaving(true);
        try {
            const body = buildBody();
            const res = isCreate
                ? await apiService.post('GEN_INV_CREATE', body)
                : await apiService.updateGeneralInvoice(voucherNoParam, body);
            const statusCode = res?.data?.statusCode ?? res?.status;

            if (statusCode === 200 || statusCode === 201) {
                const result = res?.data?.result ?? res?.data?.data;
                toast.success(res?.data?.message || `Invoice ${result?.voucherNo || ''} saved successfully`);
                setShowConfirm(false);
                navigate(LISTING_ROUTE);
                return;
            }
            if (statusCode === 400) {
                toast.error(res?.data?.message || 'Please correct the highlighted errors and try again.');
            } else if (statusCode === 404) {
                toast.error("Invoice not found or you don't have access to it.");
                navigate(LISTING_ROUTE);
                return;
            } else {
                toast.error('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Error saving invoice:', error);
            const statusCode = error?.response?.status;
            if (statusCode === 400) {
                toast.error(error?.response?.data?.message || 'Please correct the highlighted errors and try again.');
            } else if (statusCode === 404) {
                toast.error("Invoice not found or you don't have access to it.");
                navigate(LISTING_ROUTE);
            } else {
                toast.error('Something went wrong. Please try again.');
            }
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const handleResetClick = () => {
        setErrors({});
        setHeader(emptyHeader());
        setUnits([]);
        setLeaseNos([]);
        loadMasterData();
    };

    const handleRequestClose = () => {
        navigate(LISTING_ROUTE);
    };

    const titlePrefix = isCreate ? 'Create' : isView ? 'View' : 'Edit';
    const disabled = isView;

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between border-box2 p-3" style={{ borderRadius: '8px' }}>
                <h6 className="h6">{titlePrefix} Lease General Invoice {!isCreate ? voucherNoParam : ''}</h6>
                <div className="d-flex justify-content-end gap-1">
                    {loadState === 'ready' && !isView && (
                        saving ? (
                            <button className="mainbtn" type="button"><ClipLoader color="#ffffff" size={20} /></button>
                        ) : (
                            <button className="mainbtn" onClick={handleSaveClick} type="button"><FaSave className="center mt-1 mx-2" /> <div>Save</div></button>
                        )
                    )}
                    {isCreate && (
                        <button className="mainbtnRed" onClick={handleResetClick} type="button" disabled={saving}>
                            <RiRefreshLine className="center mt-1 mx-2" /><div>Reset</div>
                        </button>
                    )}
                    <button onClick={handleRequestClose} className="mainbtnSecondary" type="button" disabled={saving}>
                        <FaAngleLeft className="center mt-1 mx-2" /><div>Back</div>
                    </button>
                </div>
            </div>

            {loadState === 'loading' && (
                <div className="text-center p-5">
                    <ClipLoader color="#6c5ce7" size={36} />
                </div>
            )}

            {loadState === 'notfound' && (
                <div className="border-box2 p-4 mt-3 text-center">
                    <p>Invoice not found or you don't have access to it.</p>
                    <button type="button" className="mainbtn" onClick={() => navigate(LISTING_ROUTE)}>Close</button>
                </div>
            )}

            {loadState === 'error' && (
                <div className="border-box2 p-4 mt-3 text-center">
                    <p className="text-danger">{loadErrorMessage}</p>
                    <button type="button" className="mainbtn" onClick={() => loadInvoice()}>Retry</button>
                </div>
            )}

            {loadState === 'ready' && (
                <>
                    {/* Header */}
                    <div className="cost-center-section mt-3">
                        <div className="d-flex cursor-pointer slide-header" onClick={() => setHeaderTab(!headerTab)}>
                            <div className="param-label pt-1 col-2">Invoice Details</div>
                            <IoIosArrowForward size={30} className={`arrow fade-rotate ${headerTab ? 'open' : ''}`} />
                        </div>
                        <div className={`slide-content ${headerTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${headerTab ? 'slide-in' : 'slide-out'}`}>
                                <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
                                    {!isCreate && (
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Voucher No</label>
                                            <input type="text" className="p-2 form-control w-75" value={voucherNoParam || ''} disabled />
                                        </div>
                                    )}
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Unit Class</label>
                                        <Select
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            className="w-75 p-0"
                                            isDisabled={disabled}
                                            value={header.unitClass}
                                            onChange={handleUnitClassChange}
                                            options={dropdowns?.UnitClasses?.map(toOption)}
                                        />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Tenant</label>
                                        <WindowedSelect
                                            className="w-75 p-0"
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            isClearable
                                            isDisabled={disabled}
                                            value={header.tenant}
                                            onChange={handleTenantChange}
                                            options={dropdowns?.Tenants?.map(toTenantOption)}
                                        />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Property</label>
                                        <Select
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            className="w-75 p-0"
                                            isClearable
                                            isLoading={loadingProperties}
                                            isDisabled={disabled}
                                            value={header.property}
                                            onChange={handlePropertyChange}
                                            options={properties?.map(toOption)}
                                        />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Unit</label>
                                        <Select
                                            theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            menuShouldScrollIntoView={false}
                                            styles={selectStyles}
                                            className="w-75 p-0"
                                            isClearable
                                            isLoading={loadingUnits}
                                            isDisabled={disabled}
                                            value={header.unit}
                                            onChange={handleUnitChange}
                                            options={units?.map(toOption)}
                                        />
                                    </div>
                                    <div>
                                        <div className="d-flex align-items-center">
                                            <label className="form-label pt-1 start">Lease No *</label>
                                            <div className="d-flex align-items-center gap-2 w-75">
                                                <WindowedSelect
                                                    className="flex-grow-1 p-0"
                                                    theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                                    menuPortalTarget={document.body}
                                                    menuPlacement="auto"
                                                    menuShouldScrollIntoView={false}
                                                    styles={selectStyles}
                                                    isLoading={loadingLeaseNos}
                                                    isDisabled={disabled}
                                                    value={header.leaseNo}
                                                    onChange={handleLeaseNoChange}
                                                    options={leaseNos?.map(toOption)}
                                                />
                                                <label className="d-flex align-items-center gap-1 mb-0" style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                                                    <input
                                                        type="checkbox"
                                                        style={{ width: '18px', height: '18px' }}
                                                        checked={header.showAllLeases}
                                                        disabled={disabled}
                                                        onChange={(e) => handleShowAllLeasesToggle(e.target.checked)}
                                                    />
                                                    Show all
                                                </label>
                                            </div>
                                        </div>
                                        {errors.leaseNo && <div className="text-danger small">{errors.leaseNo}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Invoice Type *</label>
                                            <Select
                                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                                menuPortalTarget={document.body}
                                                menuPlacement="auto"
                                                menuShouldScrollIntoView={false}
                                                styles={selectStyles}
                                                className="w-75 p-0"
                                                isDisabled={disabled}
                                                value={header.invoiceType}
                                                onChange={handleInvoiceTypeChange}
                                                options={dropdowns?.InvoiceTypes?.map(toOption)}
                                            />
                                        </div>
                                        {errors.invoiceType && <div className="text-danger small">{errors.invoiceType}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Date *</label>
                                            <DatePicker
                                                selected={header.date}
                                                onChange={handleDateChange}
                                                dateFormat="dd-MMM-yyyy"
                                                className="p-2 form-control w-100"
                                                disabled={disabled}
                                                todayButton="Today"
                                            />
                                        </div>
                                        {errors.date && <div className="text-danger small">{errors.date}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Amount *</label>
                                            <input
                                                type="number"
                                                step="0.001"
                                                className="p-2 form-control w-75"
                                                value={header.amount}
                                                disabled={disabled}
                                                onChange={(e) => handleAmountChange(e.target.value)}
                                            />
                                        </div>
                                        {errors.amount && <div className="text-danger small">{errors.amount}</div>}
                                    </div>
                                    {!isCreate && (
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Amount Received</label>
                                            <input type="text" className="p-2 form-control w-75" value={formatAmount(loadedHeader?.amountReceived)} disabled />
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center">
                                        <label className="form-label pt-1 start">Adjustment</label>
                                        <input
                                            type="checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                            checked={header.isAdjustment}
                                            disabled={disabled}
                                            onChange={(e) => handleAdjustmentToggle(e.target.checked)}
                                        />
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Narration</label>
                                            <textarea
                                                className="p-2 form-control w-75"
                                                value={header.commonNarration}
                                                disabled={disabled}
                                                onChange={(e) => handleNarrationChange(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lines */}
                    <div className="cost-center-section mt-3">
                        <div className="d-flex cursor-pointer slide-header" onClick={() => setLinesTab(!linesTab)}>
                            <div className="param-label pt-1 col-2">Credit Accounts</div>
                            <IoIosArrowForward size={30} className={`arrow fade-rotate ${linesTab ? 'open' : ''}`} />
                        </div>
                        <div className={`slide-content ${linesTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${linesTab ? 'slide-in' : 'slide-out'}`}>
                                {errors.lines && <div className="text-danger small mb-2">{errors.lines}</div>}
                                <div className="table-container" style={{ overflowX: 'auto' }}>
                                    <table className="table table-bordered gridshad">
                                        <thead className="dynoTable">
                                            <tr>
                                                {!isView && <th>Action</th>}
                                                <th>Account Code</th>
                                                <th>Account Name</th>
                                                <th>Credit Amount</th>
                                                <th>Tax</th>
                                                <th>Inclusive</th>
                                                <th>Vat</th>
                                                <th>Total</th>
                                                <th>Narration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lines.map((line, index) => (
                                                <tr key={line.key}>
                                                    {!isView && (
                                                        <td className="text-center">
                                                            <div className="center mt-2">
                                                                <FaTrash
                                                                    size={18}
                                                                    style={{ cursor: lines.length > 1 ? 'pointer' : 'not-allowed', color: 'red', opacity: lines.length > 1 ? 1 : 0.4 }}
                                                                    onClick={() => handleRemoveLine(index)}
                                                                />
                                                            </div>
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div style={{ minWidth: '160px' }}>
                                                            <WindowedSelect
                                                                className="w-100 p-0"
                                                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                                                menuPortalTarget={document.body}
                                                                menuPlacement="auto"
                                                                menuShouldScrollIntoView={false}
                                                                styles={selectStyles}
                                                                isDisabled={isView}
                                                                value={toAccountCodeOption(line.acCode)}
                                                                onChange={(opt) => updateLine(index, { acCode: opt?.value || null })}
                                                                options={dropdowns?.Accounts?.map(toAccountCodeOption)}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ minWidth: '200px' }}>
                                                            <WindowedSelect
                                                                className="w-100 p-0"
                                                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                                                menuPortalTarget={document.body}
                                                                menuPlacement="auto"
                                                                menuShouldScrollIntoView={false}
                                                                styles={selectStyles}
                                                                isDisabled={isView}
                                                                value={toAccountNameOption(line.acCode)}
                                                                onChange={(opt) => updateLine(index, { acCode: opt?.value || null })}
                                                                options={dropdowns?.Accounts?.map(toAccountNameOption)}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="0.001"
                                                            value={line.enteredAmount}
                                                            className="p-1"
                                                            disabled={isView}
                                                            style={{ minWidth: '110px' }}
                                                            onChange={(e) => updateLine(index, { enteredAmount: e.target.value })}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div style={{ minWidth: '160px' }}>
                                                            <Select
                                                                className="w-100 p-0"
                                                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                                                menuPortalTarget={document.body}
                                                                menuPlacement="auto"
                                                                menuShouldScrollIntoView={false}
                                                                styles={selectStyles}
                                                                isDisabled={isView}
                                                                value={line.taxCode}
                                                                onChange={(opt) => updateLine(index, { taxCode: opt })}
                                                                options={taxCodes?.map(toTaxOption)}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            style={{ width: '18px', height: '18px' }}
                                                            checked={line.inclusive}
                                                            disabled={isView}
                                                            onChange={(e) => updateLine(index, { inclusive: e.target.checked })}
                                                        />
                                                    </td>
                                                    <td><input value={formatAmount(line.vat)} className="p-1" disabled style={{ minWidth: '90px' }} /></td>
                                                    <td><input value={formatAmount(line.total)} className="p-1" disabled style={{ minWidth: '90px' }} /></td>
                                                    <td>
                                                        <input
                                                            value={line.narration}
                                                            className="p-1"
                                                            disabled={isView}
                                                            style={{ minWidth: '160px' }}
                                                            onChange={(e) => updateLine(index, { narration: e.target.value })}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan={isView ? 2 : 3}><strong>Total</strong></td>
                                                <td><strong>{formatAmount(totalCredit)}</strong></td>
                                                <td></td>
                                                <td></td>
                                                <td><strong>{formatAmount(totalVat)}</strong></td>
                                                <td><strong>{formatAmount(totalAmount)}</strong></td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                    {!isView && (
                                        <div className="row p-0 m-0">
                                            <div className="mainbtn col-2 gap-2" onClick={handleAddLine} style={{ cursor: 'pointer' }}>
                                                <MdAdd color="#fff" className="center mt-1 mx-2" />
                                                <div className="center">Add New</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm {isCreate ? 'Save' : 'Update'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to {isCreate ? 'save this invoice' : `update invoice ${voucherNoParam}`}?
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setShowConfirm(false)} disabled={saving}>No</button>
                    <button type="button" className="mainbtnRed center" onClick={handleConfirmSave} disabled={saving}>
                        {saving ? <ClipLoader color="#ffffff" size={18} /> : 'Yes'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LeaseGeneralInvoiceForm;
